import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  FileText, 
  ClipboardList, 
  FileCheck, 
  PackageCheck,
  ChevronRight,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  ArrowRight,
  HeartPulse,
  Truck,
  Warehouse,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadPdf } from '../utils/downloadPdf';
import { fmtDateTime } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Traceability() {
  const { user } = useOutletContext();
  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchType, setSearchType] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedTrace, setExpandedTrace] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    fetchTraces();
  }, []);

  const fetchTraces = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.request_number) params.append('request_number', filters.request_number);
      if (filters.order_number) params.append('order_number', filters.order_number);
      if (filters.reception_number) params.append('reception_number', filters.reception_number);
      if (filters.internal_request_number) params.append('internal_request_number', filters.internal_request_number);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/reports/traceability?${params.toString()}`,
        { withCredentials: true }
      );
      setTraces(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permiso para ver reportes');
      } else {
        toast.error('Error al cargar trazabilidad');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const filters = {};
    
    if (searchType === 'np' && searchValue) {
      filters.request_number = searchValue;
    } else if (searchType === 'oc' && searchValue) {
      filters.order_number = searchValue;
    } else if (searchType === 'rec' && searchValue) {
      filters.reception_number = searchValue;
    } else if (searchType === 'opi' && searchValue) {
      filters.internal_request_number = searchValue;
    }
    
    if (dateFrom) filters.date_from = new Date(dateFrom).toISOString();
    if (dateTo) filters.date_to = new Date(dateTo).toISOString();
    
    fetchTraces(filters);
  };

  const handleClearFilters = () => {
    setSearchType('all');
    setSearchValue('');
    setDateFrom('');
    setDateTo('');
    fetchTraces();
  };

  const handleExportPdf = async (traceId) => {
    setExporting(traceId);
    try {
      await downloadPdf(
        `/reports/traceability/${traceId}/pdf`,
        `trazabilidad_${traceId}.pdf`
      );
    } catch (error) {
      toast.error('Error al generar PDF');
    } finally {
      setExporting(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      complete: { label: 'Completado', className: 'bg-green-100 text-green-800' },
      pending_approval: { label: 'Pendiente Aprobación', className: 'bg-yellow-100 text-yellow-800' },
      pending_oc: { label: 'Pendiente OC', className: 'bg-blue-100 text-blue-800' },
      pending_reception: { label: 'Pendiente Recepción', className: 'bg-purple-100 text-purple-800' },
      rejected: { label: 'Rechazado', className: 'bg-red-100 text-red-800' },
      incomplete: { label: 'Incompleto', className: 'bg-gray-100 text-gray-800' },
      opi_only: { label: 'OPI sin compra externa', className: 'bg-slate-100 text-slate-800' },
    };
    
    const config = statusConfig[status] || statusConfig.incomplete;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  // Traduce status crudos del backend (NP, OC, Recepción, OPI) a español
  const translateStatus = (status) => {
    if (!status) return '-';
    const map = {
      // Comunes
      complete: 'Completado',
      completed: 'Completado',
      pending: 'Pendiente',
      approved: 'Aprobado',
      received: 'Recibida',
      rejected: 'Rechazado',
      cancelled: 'Cancelado',
      // Notas de Pedido
      submitted: 'Pendiente Aprobación',
      partially_approved: 'Aprobación Parcial',
      partial: 'Parcial',
      // Órdenes de Compra
      issued: 'Emitida',
      partially_received: 'Recepción Parcial',
      received_with_shortage: 'Recibida c/Faltante',
      partial_received: 'En Revisión',
      // Recepciones
      conditional: 'Condicional',
      // OPIs
      partial_dispensed: 'Despacho Parcial',
      dispensed: 'Despachado',
      closed_partial: 'Cerrado c/ saldo',
      closed: 'Recibido en área',
      returned: 'Devuelto',
    };
    return map[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return fmtDateTime(dateStr);
    } catch {
      return dateStr;
    }
  };

  const OriginStep = ({ origen }) => {
    if (!origen) return null;
    const isOpi = origen.tipo === 'opi';
    const Icon = isOpi ? HeartPulse : Pencil;
    return (
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <div className={`p-2 rounded-full ${isOpi ? 'bg-purple-500' : 'bg-slate-500'}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-sm">Origen</span>
        </div>
        <Card className="border-l-4" style={{ borderLeftColor: isOpi ? '#a855f7' : '#64748b' }}>
          <CardContent className="p-3 space-y-1 text-sm">
            {isOpi ? (
              <>
                <div className="font-bold text-base">📋 {origen.opis.length} OPI(s)</div>
                {origen.opis.slice(0, 2).map(o => (
                  <div key={o.request_id} className="text-muted-foreground">
                    <span className="font-medium">{o.request_number}</span> · {o.area_name}
                  </div>
                ))}
                {origen.opis.length > 2 && <div className="text-xs text-muted-foreground">+{origen.opis.length - 2} más</div>}
              </>
            ) : (
              <>
                <div className="font-bold text-base">✏️ Carga directa</div>
                <div className="text-muted-foreground text-xs">Por Compras (sin OPI)</div>
                <div className="text-muted-foreground text-xs">{origen.created_by_name}</div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const DistributionStep = ({ distribucion }) => {
    if (!distribucion) return null;
    const isStockCentral = distribucion.tipo === 'stock_central';
    const isOpiOnly = distribucion.tipo === 'opi_only';
    const Icon = isStockCentral ? Warehouse : Truck;
    const dispatches = distribucion.dispatches || [];
    const pending = distribucion.opis_pending || [];
    return (
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 rounded-full bg-teal-500">
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-sm">Distribución</span>
        </div>
        <Card className="border-l-4" style={{ borderLeftColor: '#14b8a6' }}>
          <CardContent className="p-3 space-y-1 text-sm">
            {isStockCentral ? (
              <>
                <div className="font-bold text-base">📦 Reposición</div>
                <div className="text-muted-foreground text-xs">Al depósito central</div>
              </>
            ) : isOpiOnly ? (
              <>
                <div className="font-bold text-base">—</div>
                <div className="text-muted-foreground text-xs">Sin compra asociada</div>
              </>
            ) : (
              <>
                <div className="font-bold text-base">🚚 {dispatches.length} despacho(s)</div>
                {dispatches.slice(0, 2).map(d => (
                  <div key={`${d.opi_request_id}-${d.dispatch_number}`} className="text-muted-foreground">
                    <span className="font-medium">#{d.dispatch_number}</span> · {d.area_name} · {d.total_quantity}u
                  </div>
                ))}
                {pending.length > 0 && (
                  <div className="text-amber-700 text-xs font-medium">⚠ Saldo pendiente</div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const TraceStep = ({ title, icon: Icon, data, color, isActive }) => (
    <div className={`flex-1 ${!isActive ? 'opacity-40' : ''}`}>
      <div className={`flex items-center gap-2 mb-2`}>
        <div className={`p-2 rounded-full ${color}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <span className="font-semibold text-sm">{title}</span>
      </div>
      {data ? (
        <Card className="border-l-4" style={{ borderLeftColor: isActive ? '' : '#ccc' }}>
          <CardContent className="p-3 space-y-1 text-sm">
            <div className="font-bold text-base">{data.request_number || data.order_number || data.reception_number}</div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <User className="w-3 h-3" />
              <span>{data.created_by || data.received_by}</span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(data.created_at || data.received_at)}</span>
            </div>
            {data.supplier && (
              <div className="text-muted-foreground">
                Proveedor: <span className="font-medium">{data.supplier}</span>
              </div>
            )}
            <div className="text-muted-foreground">
              Items: <span className="font-medium">{data.items_count}</span>
            </div>
            {data.status && (
              <Badge variant="outline" className="text-xs">{translateStatus(data.status)}</Badge>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-2 border-gray-200">
          <CardContent className="p-3 text-center text-muted-foreground text-sm">
            Sin datos
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="traceability-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2">Reporte de Trazabilidad</h1>
        <p className="text-muted-foreground">Flujo completo: Origen (OPI o Manual) → Nota de Pedido → Orden de Compra → Recepción → Distribución</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros de Búsqueda
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search Type */}
            <div>
              <label className="text-sm font-medium mb-1 block">Buscar por</label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                data-testid="search-type-select"
              >
                <option value="all">Todos</option>
                <option value="opi">OPI (Pedido Interno)</option>
                <option value="np">Nota de Pedido</option>
                <option value="oc">Orden de Compra</option>
                <option value="rec">Recepción</option>
              </select>
            </div>

            {/* Search Value */}
            <div>
              <label className="text-sm font-medium mb-1 block">Número de documento</label>
              <Input
                placeholder={
                  searchType === 'opi' ? 'OPI-2026-0001' :
                  searchType === 'np' ? 'NP-2026-0001' :
                  searchType === 'oc' ? 'OC-2026-0001' :
                  searchType === 'rec' ? 'REC-2026-0001' :
                  'Seleccione tipo'
                }
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                disabled={searchType === 'all'}
                data-testid="search-value-input"
              />
            </div>

            {/* Date From */}
            <div>
              <label className="text-sm font-medium mb-1 block">Desde</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from-input"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="text-sm font-medium mb-1 block">Hasta</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="date-to-input"
              />
            </div>

            {/* Actions */}
            <div className="flex items-end gap-2">
              <Button onClick={handleSearch} className="flex-1" data-testid="search-button">
                <Search className="w-4 h-4 mr-2" />
                Buscar
              </Button>
              <Button variant="outline" onClick={handleClearFilters} data-testid="clear-filters-button">
                Limpiar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : traces.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No se encontraron registros de trazabilidad</p>
            <p className="text-sm">Intenta ajustar los filtros de búsqueda</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {traces.map((trace) => (
            <Card 
              key={trace.trace_id} 
              className="hover:shadow-md transition-shadow"
              data-testid="trace-card"
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-lg">
                      {trace.nota_pedido?.request_number || trace.orden_compra?.order_number || trace.recepcion?.reception_number}
                    </CardTitle>
                    {getStatusBadge(trace.status)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExportPdf(trace.trace_id)}
                      disabled={exporting === trace.trace_id}
                      data-testid="export-pdf-button"
                    >
                      {exporting === trace.trace_id ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                      ) : (
                        <FileText className="w-4 h-4 mr-2" />
                      )}
                      PDF
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedTrace(expandedTrace === trace.trace_id ? null : trace.trace_id)}
                      data-testid="expand-trace-button"
                    >
                      <ChevronRight className={`w-4 h-4 transition-transform ${expandedTrace === trace.trace_id ? 'rotate-90' : ''}`} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Timeline Visual — 5 etapas adaptativas */}
                <div className="flex items-stretch gap-2 mb-4 overflow-x-auto">
                  <OriginStep origen={trace.origen} />
                  <div className="flex items-center"><ArrowRight className="w-5 h-5 text-purple-400" /></div>
                  <TraceStep
                    title="Nota de Pedido"
                    icon={ClipboardList}
                    data={trace.nota_pedido}
                    color="bg-green-500"
                    isActive={!!trace.nota_pedido}
                  />
                  <div className="flex items-center"><ArrowRight className={`w-5 h-5 ${trace.orden_compra ? 'text-blue-500' : 'text-gray-300'}`} /></div>
                  <TraceStep
                    title="Orden de Compra"
                    icon={FileCheck}
                    data={trace.orden_compra}
                    color="bg-blue-500"
                    isActive={!!trace.orden_compra}
                  />
                  <div className="flex items-center"><ArrowRight className={`w-5 h-5 ${trace.recepcion ? 'text-amber-500' : 'text-gray-300'}`} /></div>
                  <TraceStep
                    title="Recepción"
                    icon={PackageCheck}
                    data={trace.recepcion}
                    color="bg-amber-500"
                    isActive={!!trace.recepcion}
                  />
                  <div className="flex items-center"><ArrowRight className={`w-5 h-5 ${(trace.distribucion?.dispatches?.length || trace.distribucion?.tipo === 'stock_central') ? 'text-teal-500' : 'text-gray-300'}`} /></div>
                  <DistributionStep distribucion={trace.distribucion} />
                </div>

                {/* Expanded Details */}
                {expandedTrace === trace.trace_id && (
                  <div className="border-t pt-4 mt-4 space-y-4" data-testid="trace-details">
                    {/* Origen */}
                    {trace.origen && (
                      <div className="bg-purple-50 dark:bg-purple-950/30 p-4 rounded-lg">
                        <h4 className="font-semibold text-purple-700 dark:text-purple-300 mb-3 flex items-center gap-2">
                          {trace.origen.tipo === 'opi' ? <HeartPulse className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                          Origen del Pedido
                        </h4>
                        {trace.origen.tipo === 'opi' ? (
                          <div className="space-y-2 text-sm">
                            {trace.origen.opis.map(o => (
                              <div key={o.request_id} className="border-l-2 border-purple-400 pl-3">
                                <div className="font-semibold">{o.request_number} · {o.area_name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {formatDate(o.created_at)} · {o.created_by_name} · Prioridad: {o.priority} · Estado: {o.status} · {o.items_count} item(s)
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm space-y-1">
                            <p><span className="text-muted-foreground">Tipo:</span> <strong>Carga directa por Compras</strong> (sin OPI origen)</p>
                            <p><span className="text-muted-foreground">Cargada por:</span> {trace.origen.created_by_name}</p>
                            {trace.origen.notes && <p><span className="text-muted-foreground">Notas:</span> {trace.origen.notes}</p>}
                          </div>
                        )}
                      </div>
                    )}
                    {/* NP Details */}
                    {trace.nota_pedido && (
                      <div className="bg-green-50 dark:bg-green-950 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-700 dark:text-green-300 mb-3 flex items-center gap-2">
                          <ClipboardList className="w-4 h-4" />
                          Detalle Nota de Pedido
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Número:</span>
                            <p className="font-medium">{trace.nota_pedido.request_number}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Creado por:</span>
                            <p className="font-medium">{trace.nota_pedido.created_by}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Fecha creación:</span>
                            <p className="font-medium">{formatDate(trace.nota_pedido.created_at)}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Estado:</span>
                            <p className="font-medium">{translateStatus(trace.nota_pedido.status)}</p>
                          </div>
                          {trace.nota_pedido.approved_by && (
                            <>
                              <div>
                                <span className="text-muted-foreground">Aprobado por:</span>
                                <p className="font-medium flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                  {trace.nota_pedido.approved_by}
                                </p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Fecha aprobación:</span>
                                <p className="font-medium">{formatDate(trace.nota_pedido.approved_at)}</p>
                              </div>
                            </>
                          )}
                          {trace.nota_pedido.rejected_by && (
                            <>
                              <div>
                                <span className="text-muted-foreground">Rechazado por:</span>
                                <p className="font-medium flex items-center gap-1">
                                  <XCircle className="w-3 h-3 text-red-600" />
                                  {trace.nota_pedido.rejected_by}
                                </p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Fecha rechazo:</span>
                                <p className="font-medium">{formatDate(trace.nota_pedido.rejected_at)}</p>
                              </div>
                            </>
                          )}
                        </div>
                        {trace.nota_pedido.notes && (
                          <div className="mt-3">
                            <span className="text-muted-foreground text-sm">Notas:</span>
                            <p className="text-sm">{trace.nota_pedido.notes}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* OC Details */}
                    {trace.orden_compra && (
                      <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-3 flex items-center gap-2">
                          <FileCheck className="w-4 h-4" />
                          Detalle Orden de Compra
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Número:</span>
                            <p className="font-medium">{trace.orden_compra.order_number}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Proveedor:</span>
                            <p className="font-medium">{trace.orden_compra.supplier}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Creado por:</span>
                            <p className="font-medium">{trace.orden_compra.created_by}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Fecha creación:</span>
                            <p className="font-medium">{formatDate(trace.orden_compra.created_at)}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Estado:</span>
                            <p className="font-medium">{translateStatus(trace.orden_compra.status)}</p>
                          </div>
                          {trace.orden_compra.approved_by && (
                            <div>
                              <span className="text-muted-foreground">Aprobado por:</span>
                              <p className="font-medium">{trace.orden_compra.approved_by}</p>
                            </div>
                          )}
                        </div>
                        {trace.orden_compra.observations && (
                          <div className="mt-3">
                            <span className="text-muted-foreground text-sm">Observaciones:</span>
                            <p className="text-sm">{trace.orden_compra.observations}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Reception Details */}
                    {trace.recepcion && (
                      <div className="bg-amber-50 dark:bg-amber-950 p-4 rounded-lg">
                        <h4 className="font-semibold text-amber-700 dark:text-amber-300 mb-3 flex items-center gap-2">
                          <PackageCheck className="w-4 h-4" />
                          Detalle Recepción
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Número:</span>
                            <p className="font-medium">{trace.recepcion.reception_number}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Recibido por:</span>
                            <p className="font-medium">{trace.recepcion.received_by}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Fecha recepción:</span>
                            <p className="font-medium">{formatDate(trace.recepcion.received_at)}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Estado:</span>
                            <p className="font-medium">{translateStatus(trace.recepcion.status)}</p>
                          </div>
                          {trace.recepcion.final_status && (
                            <div>
                              <span className="text-muted-foreground">Estado final:</span>
                              <p className="font-medium">{translateStatus(trace.recepcion.final_status)}</p>
                            </div>
                          )}
                        </div>
                        {trace.recepcion.deviation_notes && (
                          <div className="mt-3">
                            <span className="text-muted-foreground text-sm">Notas de desviación:</span>
                            <p className="text-sm">{trace.recepcion.deviation_notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Distribución */}
                    {trace.distribucion && (
                      <div className="bg-teal-50 dark:bg-teal-950/30 p-4 rounded-lg">
                        <h4 className="font-semibold text-teal-700 dark:text-teal-300 mb-3 flex items-center gap-2">
                          {trace.distribucion.tipo === 'stock_central' ? <Warehouse className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
                          Distribución
                        </h4>
                        {trace.distribucion.tipo === 'stock_central' && (
                          <p className="text-sm text-muted-foreground">📦 Reposición a depósito central. El stock quedó disponible para futuras salidas (consumos, transferencias, OPIs).</p>
                        )}
                        {trace.distribucion.tipo === 'opi_only' && (
                          <p className="text-sm text-muted-foreground">Esta OPI no generó una compra externa. Se atendió con stock existente o se marcó como "atendida afuera del sistema".</p>
                        )}
                        {trace.distribucion.tipo === 'opi_dispatches' && (
                          <>
                            <div className="space-y-2 text-sm">
                              {trace.distribucion.dispatches.map((d) => (
                                <div key={`${d.opi_request_id}-${d.dispatch_number}`} className="border-l-2 border-teal-400 pl-3">
                                  <div className="font-semibold">
                                    Despacho #{d.dispatch_number} → {d.area_name} ({d.opi_request_number})
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {formatDate(d.dispatched_at)} · por {d.dispatched_by_name} · Total: {d.total_quantity}u
                                  </div>
                                  <div className="text-xs mt-1">
                                    {(d.items || []).map((di, k) => (
                                      <span key={k} className="inline-block mr-2">• {di.article_name}: {di.quantity}u</span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                              {trace.distribucion.dispatches.length === 0 && (
                                <p className="text-sm text-muted-foreground">Sin despachos registrados todavía. Pasará a despachar cuando se confirme la recepción.</p>
                              )}
                            </div>
                            {trace.distribucion.opis_pending?.length > 0 && (
                              <div className="mt-3 border-t pt-3">
                                <div className="text-xs font-semibold text-amber-700 mb-2">⚠ Saldos pendientes:</div>
                                {trace.distribucion.opis_pending.map((p, i) => (
                                  <div key={i} className="text-xs text-amber-700">
                                    OPI {p.opi_request_number} ({p.area_name}): <strong>{p.pending_qty}u</strong> ({translateStatus(p.status)})
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default Traceability;
